// HistorialCasosListar.jsx
import { useEffect, useState } from "react";
import DashboardLayout from "../../controls/DashboardLayout";
import DashboardNavbar from "../../controls/DashboardNavbar";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import ManualAyuda from "../../controls/ManualAyuda";
import MDButton from "../../controls/MDButton";
import MDInput from "../../controls/MDInput";
import { Card } from "react-bootstrap";
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  TableContainer,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Badge from "@mui/material/Badge";
import { ExpandMore, FileDownload, EventNote } from "@mui/icons-material";
import ExcelJS from "exceljs";
import dayjs from "dayjs";
import { postData } from "../../Utils/Http/api";
import { fetchOrganismos } from "../../Utils/Organismo";
import getCookie from "../../Utils/getcookies";
import setCookie from "../../Utils/setcookie";

// Opciones de "Registros por pagina": las mismas que el resto de las pantallas con listado.
const OPCIONES_POR_PAGINA = [10, 25, 50, 100, 200];

// El backend acepta hasta 500 casos por pedido de eventos.
const CASOS_POR_PEDIDO_EVENTOS = 500;

const MESES_ABREV = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

// Genera los períodos de gestión disponibles: desde 202501-ENE hasta 3 meses adelante de hoy.
const generatePeriodosGestion = () => {
  const limite = dayjs().add(3, "month");
  const endYear = limite.year();
  const endMonth = limite.month();

  const periodos = [];
  let y = 2025;
  let m = 0; // enero
  while (y < endYear || (y === endYear && m <= endMonth)) {
    const yyyymm = `${y}${String(m + 1).padStart(2, "0")}`;
    periodos.push(`${yyyymm}-${MESES_ABREV[m]}`);
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return periodos;
};

function HistorialCasosListar() {
  const [filters, setFilters] = useState(() => {
    const cookieIdentificacion = getCookie("historialCasosFiltroIdentificacion") || "";
    const cookieIdOrganismo = getCookie("historialCasosFiltroIdOrganismo");
    const cookieNombreSujeto = getCookie("historialCasosFiltroNombreSujeto") || "";
    const cookieNumeroCaso = getCookie("historialCasosFiltroNumeroCaso") || "";
    const cookiePeriodoGestion = getCookie("historialCasosFiltroPeriodoGestion") || "";
    const cookieLimiteRegistros = getCookie("historialCasosFiltroLimiteRegistros");

    return {
      identificacion: cookieIdentificacion,
      idOrganismo: cookieIdOrganismo ? parseInt(cookieIdOrganismo) : null,
      nombreSujeto: cookieNombreSujeto,
      numeroCaso: cookieNumeroCaso,
      periodoGestion: cookiePeriodoGestion,
      limiteRegistros: cookieLimiteRegistros ? parseInt(cookieLimiteRegistros) : 100,
    };
  });
  const [organismos, setOrganismos] = useState([]);
  const [periodosGestion] = useState(() => generatePeriodosGestion());
  const [rawData, setRawData] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [loading, setLoading] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  const [pagina, setPagina] = useState(1);
  const [casosPorPagina, setCasosPorPagina] = useState(() => {
    const cookieValue = parseInt(getCookie("historialCasosPorPagina"), 10);
    return OPCIONES_POR_PAGINA.includes(cookieValue) ? cookieValue : 10;
  });

  // Detalle (eventos) por caso, que se busca recien cuando el usuario abre el caso:
  // { [idCaso]: { cargando, error, eventos } }
  const [eventosPorCaso, setEventosPorCaso] = useState({});
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    const loadOrganismos = async () => {
      try {
        const data = await fetchOrganismos();
        setOrganismos(data.map((o) => ({ id: o.idOrganismo, label: o.descripcion, activo: o.activo })));
      } catch (ex) {
        console.error("Error cargando organismos:", ex);
      }
    };
    loadOrganismos();
  }, []);

  // Cada busqueda nueva deja todos los casos cerrados.
  useEffect(() => {
    setExpandedIds({});
  }, [rawData]);

  // Busca los eventos de los casos indicados en un solo pedido y los guarda por caso.
  const cargarEventos = async (idCasos) => {
    setEventosPorCaso((prev) => {
      const siguiente = { ...prev };
      idCasos.forEach((id) => {
        siguiente[id] = { cargando: true, error: false, eventos: [] };
      });
      return siguiente;
    });
    try {
      const response = await postData("/HistorialCasosEventos", { idCasos });
      const porCaso = {};
      (response?.value || []).forEach((evento) => {
        (porCaso[evento.idCaso] = porCaso[evento.idCaso] || []).push(evento);
      });
      setEventosPorCaso((prev) => {
        const siguiente = { ...prev };
        idCasos.forEach((id) => {
          siguiente[id] = { cargando: false, error: false, eventos: porCaso[id] || [] };
        });
        return siguiente;
      });
    } catch (ex) {
      console.error("Error cargando eventos del historial:", ex);
      setEventosPorCaso((prev) => {
        const siguiente = { ...prev };
        idCasos.forEach((id) => {
          siguiente[id] = { cargando: false, error: true, eventos: [] };
        });
        return siguiente;
      });
    }
  };

  // Cuando el usuario abre un caso de la pagina actual que todavia no tiene su detalle, se lo pide.
  useEffect(() => {
    const pendientes = rawData
      .slice((pagina - 1) * casosPorPagina, pagina * casosPorPagina)
      .filter((caso) => expandedIds[caso.idCaso] && !eventosPorCaso[caso.idCaso])
      .map((caso) => caso.idCaso);
    if (pendientes.length > 0) {
      cargarEventos(pendientes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData, pagina, casosPorPagina, expandedIds, eventosPorCaso]);

  const reintentarEventos = (idCaso) => {
    setEventosPorCaso((prev) => {
      const siguiente = { ...prev };
      delete siguiente[idCaso];
      return siguiente;
    });
  };

  const handleCasosPorPaginaChange = (event, newValue) => {
    const valor = parseInt(newValue, 10);
    setCasosPorPagina(valor);
    setCookie("historialCasosPorPagina", valor, 43200);
    setPagina(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleOrganismoChange = (event, newValue) => {
    setFilters({ ...filters, idOrganismo: newValue?.id || null });
  };

  const handlePeriodoGestionChange = (event, newValue) => {
    setFilters({ ...filters, periodoGestion: newValue || "" });
  };

  const handleAccordionToggle = (idCaso) => (event, isExpanded) => {
    setExpandedIds((prev) => ({ ...prev, [idCaso]: isExpanded }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = async () => {
    setCookie("historialCasosFiltroIdentificacion", filters.identificacion, 43200);
    setCookie("historialCasosFiltroIdOrganismo", filters.idOrganismo || "", 43200);
    setCookie("historialCasosFiltroNombreSujeto", filters.nombreSujeto, 43200);
    setCookie("historialCasosFiltroNumeroCaso", filters.numeroCaso, 43200);
    setCookie("historialCasosFiltroPeriodoGestion", filters.periodoGestion, 43200);
    setCookie("historialCasosFiltroLimiteRegistros", filters.limiteRegistros, 43200);

    setLoading(true);
    setBuscado(true);
    try {
      const req = {
        identificacion: filters.identificacion || "",
        idOrganismo: filters.idOrganismo || 0,
        nombreSujeto: filters.nombreSujeto || "",
        idCaso: filters.numeroCaso ? parseInt(filters.numeroCaso) : 0,
        periodoGestion: filters.periodoGestion || "",
        limiteRegistros: filters.limiteRegistros ? parseInt(filters.limiteRegistros) : 100,
        incluirEventos: false,
      };

      const response = await postData("/HistorialCasosListar", req);
      setEventosPorCaso({});
      setRawData(response?.value || []);
      setPagina(1);
    } catch (ex) {
      console.error("Error cargando historial de casos:", ex);
      setSnackbar({ open: true, msg: "Error al cargar el historial de casos", severity: "error" });
      setRawData([]);
    } finally {
      setLoading(false);
    }
  };

  const buildWorkbook = (eventosMap) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Historial de Casos");

    worksheet.columns = [
      { header: "N° DE CASO", key: "idCaso", width: 12 },
      { header: "ORGANISMO", key: "organismo", width: 30 },
      { header: "TIPO IDENTIFICACIÓN", key: "tipoIdentificacion", width: 18 },
      { header: "IDENTIFICACIÓN", key: "identificacion", width: 16 },
      { header: "SUJETO", key: "nombreSujeto", width: 30 },
      { header: "USUARIO ASIGNADO", key: "usuarioAsignado", width: 22 },
      { header: "PERIODO DE GESTIÓN", key: "periodoGestion", width: 18 },
      { header: "ESTADO DEL CASO", key: "estadoCaso", width: 20 },
      { header: "FECHA DEL CASO", key: "fechaCaso", width: 16 },
      { header: "ESTADO DE CIERRE", key: "tipoCierreDescripcion", width: 18 },
      { header: "FECHA EVENTO", key: "fechaEvento", width: 18 },
      { header: "USUARIO EVENTO", key: "usuarioEvento", width: 22 },
      { header: "ESTADO EVENTO", key: "estadoEvento", width: 20 },
      { header: "TIPO DE EVENTO", key: "tipoEvento", width: 26 },
      { header: "OBSERVACIONES", key: "observaciones", width: 60 },
    ];

    worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    worksheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1976D2" } };
    worksheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };

    rawData.forEach((caso) => {
      const base = {
        idCaso: caso.idCaso,
        organismo: caso.organismo || "",
        tipoIdentificacion: caso.tipoIdentificacion || "",
        identificacion: caso.identificacion || "",
        nombreSujeto: caso.nombreSujeto || "",
        usuarioAsignado: (caso.usuarioAsignado || "").toUpperCase(),
        periodoGestion: caso.periodoGestion || "",
        estadoCaso: caso.estado || "",
        fechaCaso: caso.fecha ? dayjs(caso.fecha).format("DD/MM/YYYY") : "",
        tipoCierreDescripcion: caso.tipoCierreDescripcion || "",
      };

      const eventosCaso = eventosMap[caso.idCaso] || [];
      if (eventosCaso.length === 0) {
        worksheet.addRow({ ...base, fechaEvento: "", usuarioEvento: "", estadoEvento: "", tipoEvento: "", observaciones: "" });
      } else {
        eventosCaso.forEach((evento) => {
          worksheet.addRow({
            ...base,
            fechaEvento: evento.fecha ? dayjs(evento.fecha).format("DD/MM/YYYY HH:mm") : "",
            usuarioEvento: (evento.usuarioNombre || "").toUpperCase(),
            estadoEvento: evento.estadoDescripcion || "",
            tipoEvento: evento.eventoTipo || "",
            observaciones: evento.observaciones || "",
          });
        });
      }
    });

    return workbook;
  };

  // Eventos de todos los casos de la lista: usa los que ya se abrieron y pide el resto por tandas.
  const obtenerEventosParaExportar = async () => {
    const eventosMap = {};
    const faltantes = [];
    rawData.forEach((caso) => {
      const cargado = eventosPorCaso[caso.idCaso];
      if (cargado && !cargado.cargando && !cargado.error) {
        eventosMap[caso.idCaso] = cargado.eventos;
      } else {
        faltantes.push(caso.idCaso);
      }
    });
    for (let i = 0; i < faltantes.length; i += CASOS_POR_PEDIDO_EVENTOS) {
      const tanda = faltantes.slice(i, i + CASOS_POR_PEDIDO_EVENTOS);
      const response = await postData("/HistorialCasosEventos", { idCasos: tanda });
      (response?.value || []).forEach((evento) => {
        (eventosMap[evento.idCaso] = eventosMap[evento.idCaso] || []).push(evento);
      });
    }
    return eventosMap;
  };

  const handleExportarExcel = async () => {
    if (rawData.length === 0) {
      setSnackbar({ open: true, msg: "No hay datos para exportar", severity: "warning" });
      return;
    }
    setExportando(true);
    try {
      const workbook = buildWorkbook(await obtenerEventosParaExportar());
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Historial_Casos_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, msg: "Reporte exportado correctamente", severity: "success" });
    } catch (ex) {
      console.error("Error exportando a Excel:", ex);
      setSnackbar({ open: true, msg: "Error al exportar el reporte", severity: "error" });
    } finally {
      setExportando(false);
    }
  };

  const totalPaginas = Math.ceil(rawData.length / casosPorPagina);
  const casosPagina = rawData.slice((pagina - 1) * casosPorPagina, pagina * casosPorPagina);

  const handlePaginaChange = (event, nuevaPagina) => {
    setPagina(nuevaPagina);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Historial de Casos
                </MDTypography>
                <ManualAyuda seccion="Historial de Casos (reporte para municipalidades)" />
              </MDBox>

              <MDBox pt={3} py={3} px={2}>
                <MDBox mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  <MDInput
                    name="identificacion"
                    label="Identificación"
                    value={filters.identificacion}
                    onChange={handleFilterChange}
                    onKeyDown={handleKeyDown}
                    sx={{ width: 200, height: 50 }}
                  />
                  <Autocomplete
                    options={organismos}
                    getOptionLabel={(option) => option.label || ""}
                    getOptionDisabled={(option) => !option.activo}
                    value={organismos.find((o) => o.id === filters.idOrganismo) || null}
                    onChange={handleOrganismoChange}
                    renderInput={(params) => <TextField {...params} label="Organismo" />}
                    sx={{ width: 300 }}
                  />
                  <MDInput
                    name="nombreSujeto"
                    label="Apellido y Nombre"
                    value={filters.nombreSujeto}
                    onChange={handleFilterChange}
                    onKeyDown={handleKeyDown}
                    sx={{ width: 220, height: 50 }}
                  />
                  <MDInput
                    name="numeroCaso"
                    label="Nro. de Caso"
                    type="number"
                    value={filters.numeroCaso}
                    onChange={handleFilterChange}
                    onKeyDown={handleKeyDown}
                    sx={{ width: 160, height: 50 }}
                  />
                  <Autocomplete
                    options={periodosGestion}
                    value={filters.periodoGestion || null}
                    onChange={handlePeriodoGestionChange}
                    renderInput={(params) => <TextField {...params} label="Periodo de Gestión" />}
                    sx={{ width: 200 }}
                  />
                  <MDInput
                    name="limiteRegistros"
                    label="Límite de Registros"
                    type="number"
                    value={filters.limiteRegistros}
                    onChange={handleFilterChange}
                    onKeyDown={handleKeyDown}
                    sx={{ width: 160, height: 50 }}
                  />
                  <MDButton variant="gradient" color="info" onClick={handleSearch} disabled={loading}>
                    {loading ? <CircularProgress size={18} color="inherit" /> : "Buscar"}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="success"
                    startIcon={<FileDownload />}
                    onClick={handleExportarExcel}
                    disabled={loading || exportando || rawData.length === 0}
                  >
                    {exportando ? <CircularProgress size={18} color="inherit" /> : "Exportar"}
                  </MDButton>
                </MDBox>

                {loading && (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                )}

                {!loading && buscado && rawData.length === 0 && (
                  <MDBox py={4} textAlign="center">
                    <MDTypography variant="button" color="text">
                      No se encontraron casos que coincidan con los filtros.
                    </MDTypography>
                  </MDBox>
                )}

                {!loading && rawData.length > 0 && (
                  <MDBox mb={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <MDBox display="flex" alignItems="center">
                      <Autocomplete
                        disableClearable
                        value={casosPorPagina.toString()}
                        options={OPCIONES_POR_PAGINA.map((n) => n.toString())}
                        onChange={handleCasosPorPaginaChange}
                        size="small"
                        sx={{ width: "5rem" }}
                        renderInput={(params) => <MDInput {...params} />}
                      />
                      <MDTypography variant="caption" color="secondary">
                        &nbsp;&nbsp;Registros por pagina
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text">
                      {rawData.length} casos encontrados
                      {totalPaginas > 1 && ` — página ${pagina} de ${totalPaginas}`}
                    </MDTypography>
                  </MDBox>
                )}

                {!loading &&
                  casosPagina.map((caso) => (
                    <Accordion
                      key={caso.idCaso}
                      expanded={!!expandedIds[caso.idCaso]}
                      onChange={handleAccordionToggle(caso.idCaso)}
                      TransitionProps={{ unmountOnExit: true }}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%" mr={1} gap={2}>
                          <MDBox display="flex" flexDirection="column" gap={0.5} minWidth={0}>
                            <MDBox display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                              <MDTypography variant="button" fontWeight="bold">
                                Caso #{caso.idCaso}
                              </MDTypography>
                              <MDTypography variant="caption" color="text">
                                {caso.organismo}
                              </MDTypography>
                            </MDBox>
                            <MDTypography variant="button" fontWeight="medium" sx={{ color: "#344767" }}>
                              {caso.nombreSujeto}
                              <MDTypography component="span" variant="caption" color="text" fontWeight="regular">
                                {"  ·  "}
                                {caso.tipoIdentificacion}: {caso.identificacion}
                              </MDTypography>
                            </MDTypography>
                            <MDBox display="flex" gap={0.75} flexWrap="wrap" mt={0.25}>
                              <Chip size="small" variant="outlined" label={`Período: ${caso.periodoGestion || "N/A"}`} />
                              <Chip size="small" variant="outlined" label={`Asignado: ${caso.usuarioAsignado || "Sin asignar"}`} />
                              {caso.estado && <Chip size="small" variant="outlined" color="info" label={caso.estado} />}
                              {caso.tipoCierreDescripcion && caso.tipoCierreDescripcion !== "SIN CERRAR" && (
                                <Chip size="small" variant="outlined" color="warning" label={caso.tipoCierreDescripcion} />
                              )}
                            </MDBox>
                          </MDBox>
                          <Badge
                            badgeContent={eventosPorCaso[caso.idCaso]?.eventos?.length ?? caso.cantidadEventos ?? 0}
                            color="info"
                            max={999}
                            showZero
                          >
                            <EventNote color="action" />
                          </Badge>
                        </MDBox>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        {!eventosPorCaso[caso.idCaso] || eventosPorCaso[caso.idCaso].cargando ? (
                          <MDBox display="flex" alignItems="center" gap={1} px={2} py={1.5}>
                            <CircularProgress size={16} />
                            <MDTypography variant="caption" color="text">
                              Cargando eventos...
                            </MDTypography>
                          </MDBox>
                        ) : eventosPorCaso[caso.idCaso].error ? (
                          <MDBox display="flex" alignItems="center" gap={1} px={2} py={1}>
                            <MDTypography variant="caption" color="error">
                              No se pudo cargar el detalle del caso.
                            </MDTypography>
                            <MDButton size="small" variant="text" color="info" onClick={() => reintentarEventos(caso.idCaso)}>
                              Reintentar
                            </MDButton>
                          </MDBox>
                        ) : eventosPorCaso[caso.idCaso].eventos.length > 0 ? (
                          <>
                            <MDBox
                              px={2}
                              py={1}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ backgroundColor: "#f8f9fa", borderTop: "1px solid #e9ecef", borderBottom: "1px solid #e9ecef" }}
                            >
                              <MDTypography variant="caption" fontWeight="bold" color="text">
                                {eventosPorCaso[caso.idCaso].eventos.length}{" "}
                                {eventosPorCaso[caso.idCaso].eventos.length === 1 ? "evento" : "eventos"}
                              </MDTypography>
                              <MDTypography variant="caption" color="secondary">
                                Del más reciente al más antiguo
                              </MDTypography>
                            </MDBox>
                            <TableContainer sx={{ maxHeight: 480 }}>
                              <Table
                                size="small"
                                stickyHeader
                                sx={{
                                  minWidth: 760,
                                  "& .MuiTableCell-root": {
                                    fontSize: "0.8rem",
                                    padding: "10px 14px",
                                    verticalAlign: "top",
                                    borderBottom: "1px solid #eef0f3",
                                  },
                                  // El tema de la aplicacion dibuja el encabezado como bloque: se fuerza el
                                  // comportamiento normal de tabla para que las columnas queden alineadas.
                                  "& .MuiTableHead-root": { display: "table-header-group" },
                                  "& .MuiTableCell-head": {
                                    backgroundColor: "#f8f9fa",
                                    color: "#7b809a",
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.04em",
                                    textTransform: "uppercase",
                                    whiteSpace: "nowrap",
                                  },
                                  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": { backgroundColor: "#fbfcfd" },
                                  "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: "#f0f6ff" },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Usuario</TableCell>
                                    <TableCell>Estado</TableCell>
                                    <TableCell>Tipo de evento</TableCell>
                                    <TableCell>Observaciones</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {eventosPorCaso[caso.idCaso].eventos.map((evento) => (
                                    <TableRow key={evento.idCasoEvento}>
                                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        <div style={{ fontWeight: 600 }}>
                                          {evento.fecha ? dayjs(evento.fecha).format("DD/MM/YYYY") : ""}
                                        </div>
                                        <div style={{ color: "#7b809a" }}>
                                          {evento.fecha ? dayjs(evento.fecha).format("HH:mm") : ""}
                                        </div>
                                      </TableCell>
                                      <TableCell sx={{ wordBreak: "break-word" }}>{evento.usuarioNombre}</TableCell>
                                      <TableCell>
                                        {evento.estadoDescripcion ? (
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                            label={evento.estadoDescripcion}
                                            sx={{ height: "auto", maxWidth: "100%", "& .MuiChip-label": { display: "block", whiteSpace: "normal", py: "3px" } }}
                                          />
                                        ) : null}
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 500, wordBreak: "break-word" }}>{evento.eventoTipo}</TableCell>
                                      <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#495057" }}>
                                        {evento.observaciones || "—"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : (
                          <MDBox px={2} py={2} sx={{ backgroundColor: "#f8f9fa", borderTop: "1px solid #e9ecef" }}>
                            <MDTypography variant="caption" color="secondary">
                              Este caso todavía no tiene eventos registrados.
                            </MDTypography>
                          </MDBox>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}

                {!loading && totalPaginas > 1 && (
                  <MDBox display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={totalPaginas}
                      page={pagina}
                      onChange={handlePaginaChange}
                      color="info"
                      showFirstButton
                      showLastButton
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <MuiAlert severity={snackbar.severity} elevation={6} variant="filled">
          {snackbar.msg}
        </MuiAlert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default HistorialCasosListar;
